import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { ProductFeedUpdateRequestSchema } from "@polifonia/interlude/models";
import { theme } from "@polifonia/theme";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { DownloadTemplates } from "@/pages/applications/pages/productFeeds/components/DownloadTemplates";
import { FeedCheckSummary } from "@/pages/applications/pages/productFeeds/components/FeedCheckSummary";
import { FeedNameField } from "@/pages/applications/pages/productFeeds/components/FeedNameField";
import { SyncFrequencyField } from "@/pages/applications/pages/productFeeds/components/SyncFrequencyField";
import { SyncStatus } from "@/pages/applications/pages/productFeeds/components/SyncStatus";
import { useValidateUrl } from "@/pages/applications/pages/productFeeds/hooks/useValidateUrl";

type ProductFeed = InterludeTypes["ProductFeed"];

const cssDescription = css`
  color: ${theme.colors.staticFgNote};
`;

const cssFieldLabel = css`
  color: ${theme.colors.staticFgPrimary};
`;

const cssFormContainer = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 600px;
  width: 100%;
`;

const cssButtonContainer = css({
  display: "flex",
  gap: "12px",
  marginTop: "8px",
});

interface UrlFeedFormProps {
  feed: ProductFeed;
}

export const UrlFeedForm: React.FC<UrlFeedFormProps> = function UrlFeedForm({
  feed,
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [feedName, setFeedName] = useState(feed.name);
  const [feedUrl, setFeedUrl] = useState(feed.feedUrl);
  const [syncFrequency, setSyncFrequency] = useState<"daily">("daily");

  const validateUrl = useValidateUrl();

  const handleUpdateError = useHandler((error: Error) => {
    if (error instanceof AxiosError && error.response?.status === 409) {
      enqueueSnackbar(
        t("productFeed.detailPage.error.syncing", {
          feedName: feed.name,
        }),
        {
          variant: "error",
          autoHideDuration: 5000,
        },
      );
    } else {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      enqueueSnackbar(
        `${t("productFeed.urlFeed.error.updateFailed")}: ${errorMessage}`,
        {
          variant: "error",
        },
      );
    }
  });

  const updateFeedMutation = interlude.productFeed.useUpdateFeed(
    {
      params: {
        feedId: feed.id,
      },
    },
    {
      onError: handleUpdateError,
    },
  );

  const isSubmitting = updateFeedMutation.isPending;

  const isSyncing = feed.status === "syncing";

  const handleSubmit = useHandler(async () => {
    if (isSyncing) {
      enqueueSnackbar(
        t("productFeed.detailPage.error.syncing", {
          feedName: feed.name,
        }),
        {
          variant: "error",
          autoHideDuration: 5000,
        },
      );
      return;
    }

    if (!feedName.trim()) {
      enqueueSnackbar(t("productFeed.urlFeed.error.nameRequired"), {
        variant: "error",
      });
      return;
    }

    if (!feedUrl.trim()) {
      enqueueSnackbar(t("productFeed.urlFeed.error.urlRequired"), {
        variant: "error",
      });
      return;
    }

    if (!validateUrl(feedUrl.trim())) {
      return;
    }

    // Let onError callback handle mutation errors
    updateFeedMutation
      .mutateAsync({
        name: feedName.trim(),
        feedUrl: feedUrl.trim(),
        filename: null,
      } satisfies z.infer<typeof ProductFeedUpdateRequestSchema>)
      .catch(() => {
        // Suppress unhandled promise rejection warnings
        // Error is already handled by onError callback
      });
  });

  const handleManualRefresh = useHandler(async () => {
    // Trigger sync by calling update API with current feed values
    // Let onError callback handle mutation errors
    updateFeedMutation
      .mutateAsync({
        name: feed.name,
        feedUrl: feed.feedUrl,
        filename: null,
      } satisfies z.infer<typeof ProductFeedUpdateRequestSchema>)
      .catch(() => {
        // Suppress unhandled promise rejection warnings
        // Error is already handled by onError callback
      });
  });

  return (
    <Stack css={cssFormContainer}>
      <Typography variant="body" css={cssDescription}>
        {t("productFeed.urlFeed.description")}
      </Typography>

      <FeedNameField
        value={feedName}
        onChange={setFeedName}
        disabled={isSubmitting || isSyncing}
        translationPrefix="urlFeed"
      />

      <DownloadTemplates />

      <Stack direction="column" gap={1}>
        <Typography variant="body" css={cssFieldLabel}>
          {t("productFeed.urlFeed.feedUrl")}
        </Typography>
        <TextField
          placeholder={t("productFeed.urlFeed.feedUrlPlaceholder")}
          value={feedUrl}
          onChange={(e) => setFeedUrl(e.target.value)}
          fullWidth
          required
          disabled={isSubmitting || isSyncing}
          type="url"
        />
      </Stack>

      <SyncFrequencyField
        value={syncFrequency}
        onChange={setSyncFrequency}
        disabled={true}
      />

      <SyncStatus
        feed={feed}
        onManualRefresh={handleManualRefresh}
        isRefreshing={isSubmitting}
      />
      <FeedCheckSummary feed={feed} />

      <div css={cssButtonContainer}>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={
            !feedName.trim() || !feedUrl.trim() || isSubmitting || isSyncing
          }
        >
          {t("productFeed.urlFeed.updateButton")}
        </Button>
      </div>
    </Stack>
  );
};
